import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Upload, message, Typography } from 'antd';
import { MailOutlined, SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import axios from '../../services/axiosInstance';
// Assuming a shared axios instance, or we can use standard axios.
// If 'axiosInstance' doesn't exist, we'll need to check the project structure.
// Based on VinnyChat.jsx, it uses '../../services/axiosInstance'.

const { TextArea } = Input;
const { Text } = Typography;

const MAX_SCREENSHOT_SIZE_MB = 5;

const SupportTicketForm = ({
    onSubmitSuccess,
    initialContext = '',
    initialEmail = null, // Logged-in user's email — pre-fills the field instead of leaving it blank
    apiEndpoint = '/vinny/support/raise-ticket'
}) => {
    const [loading, setLoading] = useState(false);
    const [screenshot, setScreenshot] = useState(null);
    const [form] = Form.useForm();

    // Pre-fill once the caller's user info resolves (it can arrive after
    // this component has already mounted, since it depends on an API call).
    useEffect(() => {
        if (initialEmail) {
            form.setFieldsValue({ email: initialEmail });
        }
    }, [initialEmail, form]);

    const beforeUploadScreenshot = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('Only image files can be attached.');
            return Upload.LIST_IGNORE;
        }
        const isUnderLimit = file.size / 1024 / 1024 < MAX_SCREENSHOT_SIZE_MB;
        if (!isUnderLimit) {
            message.error(`Screenshot must be smaller than ${MAX_SCREENSHOT_SIZE_MB}MB.`);
            return Upload.LIST_IGNORE;
        }
        setScreenshot(file);
        // Returning false keeps antd from auto-uploading; we send the file
        // ourselves as part of the ticket submission below.
        return false;
    };

    const removeScreenshot = () => setScreenshot(null);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            // multipart/form-data so an attached screenshot rides along with
            // the same request as the ticket fields (simpler than a second
            // upload round-trip, and the backend only needs one endpoint).
            const formData = new FormData();
            formData.append('email', values.email);
            formData.append('issue', values.issue);
            formData.append('context', initialContext);
            if (screenshot) {
                formData.append('screenshot', screenshot);
            }

            // Don't hardcode 'multipart/form-data': the shared axios instance
            // defaults Content-Type to 'application/json', and a FormData
            // body needs the browser to compute its own boundary parameter
            // (e.g. 'multipart/form-data; boundary=...'). Setting it to
            // undefined here overrides that default and lets axios/XHR set
            // the correct header automatically from the FormData body.
            await axios.post(apiEndpoint, formData, {
                headers: { 'Content-Type': undefined },
            });

            message.success('Support ticket raised successfully! We will contact you shortly.');
            form.resetFields();
            setScreenshot(null);
            if (initialEmail) {
                form.setFieldsValue({ email: initialEmail });
            }
            if (onSubmitSuccess) {
                onSubmitSuccess();
            }
        } catch (error) {
            console.error('Failed to raise ticket:', error);
            message.error(error?.response?.data?.message || 'Failed to submit ticket. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            className="vinny-support-card"
            style={{
                width: '100%',
                marginTop: 16,
                marginBottom: 16,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                border: 'none',
                borderRadius: '16px',
                overflow: 'hidden',
                background: '#ffffff'
            }}
            bodyStyle={{ padding: '24px' }}
        >
            <div style={{ marginBottom: 20, textAlign: 'center' }}>
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: '#e6f7ff',
                    marginBottom: '12px'
                }}>
                    <MailOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#262626' }}>
                    Raise a Support Ticket
                </div>
                <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: '13px', lineHeight: '1.5' }}>
                    We're sorry using the chatbot didn't work out. Share the details and we'll escalate this to a human expert.
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                size="large"
                initialValues={{ email: initialEmail || undefined }}
            >
                <Form.Item
                    name="email"
                    rules={[
                        { type: 'email', message: 'Please enter a valid email!' },
                        { required: true, message: 'Email is required to contact you.' },
                    ]}
                >
                    <Input
                        prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                        placeholder="Your Email Address"
                        style={{ borderRadius: '12px', fontSize: '14px' }}
                    />
                </Form.Item>

                <Form.Item
                    name="issue"
                    rules={[{ required: true, message: 'Please describe the issue!' }]}
                >
                    <TextArea
                        rows={4}
                        placeholder="Tell us what went wrong..."
                        style={{ borderRadius: '12px', fontSize: '14px', resize: 'none' }}
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 16 }}>
                    <Upload
                        accept="image/*"
                        maxCount={1}
                        beforeUpload={beforeUploadScreenshot}
                        onRemove={removeScreenshot}
                        fileList={screenshot ? [{ uid: '-1', name: screenshot.name, status: 'done' }] : []}
                    >
                        <Button icon={<PaperClipOutlined />} style={{ borderRadius: '12px' }}>
                            Attach Screenshot
                        </Button>
                    </Upload>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        icon={<SendOutlined />}
                        style={{
                            borderRadius: '24px',
                            height: '44px',
                            fontSize: '15px',
                            fontWeight: '500',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Raise Support Ticket
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default SupportTicketForm;
